/**
 * The valuer's feedback screen (Phase 7): the annotated copy the reviewer sent, their
 * notes, and a way to resubmit a revised report against this same request. Reads
 * (report_annotations, feedback_dispatches) go straight to Supabase - RLS's
 * owns_report_as_valuer() policy (see the Phase 7 migration) scopes them to the valuer's
 * own reports, same read pattern the reviewer dashboard already uses.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchAnnotationsForReport, fetchDispatchesForReport } from '../../reviewer/queries';
import type { AnnotationRow } from '../../reviewer/types';
import {
  fetchFeedbackDocumentUrl,
  markFeedbackViewed,
  resubmitReport,
  ApiError,
  type ResubmitResult,
} from '../../api';
import { validateFile, type FileProblem } from '../../lib/files';
import { formatDate } from '../../lib/status';
import { Banner, Button, Spinner, Tile } from '../../components/ui';
import { PdfViewer, type PdfViewerHandle } from '../../components/PdfViewer';
import { DropZone } from '../../components/DropZone';

const TYPE_LABEL: Record<AnnotationRow['type'], string> = {
  highlight: 'Highlight',
  note: 'Sticky note',
  shape: 'Shape',
};

interface FeedbackScreenProps {
  reportId: string;
  address: string | null;
  onBack: () => void;
  onResubmitted: () => void;
}

type LoadState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready' };

export function FeedbackScreen({ reportId, address, onBack, onResubmitted }: FeedbackScreenProps) {
  const viewerRef = useRef<PdfViewerHandle>(null);
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [dispatchedAt, setDispatchedAt] = useState<string | null>(null);
  const [notes, setNotes] = useState<AnnotationRow[]>([]);

  const [report, setReport] = useState<File | null>(null);
  const [reportProblem, setReportProblem] = useState<FileProblem>(null);
  const [loi, setLoi] = useState<File | null>(null);
  const [loiProblem, setLoiProblem] = useState<FileProblem>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitProgress, setResubmitProgress] = useState(0);
  const [resubmitError, setResubmitError] = useState<string | null>(null);
  const [resubmitResult, setResubmitResult] = useState<ResubmitResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoad({ status: 'loading' });
    Promise.all([
      fetchFeedbackDocumentUrl(reportId),
      fetchDispatchesForReport(reportId),
      fetchAnnotationsForReport(reportId),
    ])
      .then(([doc, dispatches, annotations]) => {
        if (cancelled) return;
        setDocumentUrl(doc.url);
        setDispatchedAt(dispatches[0]?.dispatchedAt ?? null);
        setNotes(annotations);
        setLoad({ status: 'ready' });
        void markFeedbackViewed(reportId).catch(() => {});
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad({ status: 'error', message: err instanceof Error ? err.message : 'Could not load this feedback.' });
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const chooseReport = (file: File) => {
    const problem = validateFile(file);
    setReportProblem(problem);
    setReport(problem ? null : file);
  };
  const chooseLoi = (file: File) => {
    const problem = validateFile(file);
    setLoiProblem(problem);
    setLoi(problem ? null : file);
  };
  const canResubmit = !!report && !reportProblem && !!loi && !loiProblem && !resubmitting;

  const submitResubmission = async () => {
    if (!canResubmit || !report || !loi) return;
    setResubmitError(null);
    setResubmitProgress(0);
    setResubmitting(true);
    try {
      const result = await resubmitReport({ reportId, report, letterOfInstruction: loi, onProgress: setResubmitProgress });
      setResubmitResult(result);
    } catch (err) {
      setResubmitError(err instanceof ApiError ? err.message : 'The resubmission didn’t go through. Please try again.');
    } finally {
      setResubmitting(false);
    }
  };

  const orderedNotes = notes
    .slice()
    .sort((a, b) => a.page - b.page || new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  if (resubmitResult) {
    return (
      <main className="main">
        <Tile className="tile--pad-lg">
          <div className="confirm">
            <div className="confirm__mark" aria-hidden="true">
              ✓
            </div>
            <h1 className="confirm__title">Thanks — resubmitted</h1>
            <p className="confirm__body">
              Your revised report for <strong>{address || 'this property'}</strong> (revision{' '}
              {resubmitResult.revisionNumber}) is now being reviewed automatically.
            </p>
            <div className="confirm__actions">
              <Button onClick={onResubmitted}>Back to my uploads</Button>
            </div>
          </div>
        </Tile>
      </main>
    );
  }

  return (
    <main className="main main--wide">
      <div className="page-head">
        <div>
          <Button variant="ghost" onClick={onBack}>
            ← Back to my uploads
          </Button>
          <h1 className="page-title">{address || 'Address not recorded'}</h1>
        </div>
      </div>

      {load.status === 'loading' ? (
        <Tile>
          <Spinner label="Loading your feedback…" />
        </Tile>
      ) : null}

      {load.status === 'error' ? <Banner tone="error">{load.message}</Banner> : null}

      {load.status === 'ready' ? (
        <div className="workspace">
          <div className="workspace__docs">
            {dispatchedAt ? <p className="muted">Feedback sent {formatDate(dispatchedAt)}</p> : null}
            <PdfViewer ref={viewerRef} url={documentUrl} />
          </div>

          <div className="workspace__results stack">
            <section className="section">
              <div className="section__head">
                <h2 className="section__title">Reviewer feedback</h2>
              </div>
              {orderedNotes.length === 0 ? (
                <Tile>
                  <p className="muted">No notes were left on this round.</p>
                </Tile>
              ) : (
                <Tile className="stack">
                  {orderedNotes.map((n, i) => (
                    <div key={n.id} className="markup-note markup-note--readonly">
                      <div className="markup-note__head">
                        <button
                          type="button"
                          className="markup-note__jump"
                          onClick={() => viewerRef.current?.goToPage(n.page)}
                        >
                          #{i + 1} · page {n.page} · {TYPE_LABEL[n.type]}
                        </button>
                      </div>
                      {n.text ? <p>{n.text}</p> : <p className="muted">No note text.</p>}
                    </div>
                  ))}
                </Tile>
              )}
            </section>

            <section className="section">
              <div className="section__head">
                <h2 className="section__title">Resubmit a revised report</h2>
              </div>
              <Tile className="tile--pad-lg stack">
                {resubmitError ? <Banner tone="error">{resubmitError}</Banner> : null}
                <p className="muted">
                  Attach the updated valuation report and letter of instruction. This resubmission will be
                  reviewed as a new round against this same request.
                </p>
                <div className="dz-group">
                  <DropZone
                    id="resubmit-report"
                    title="Valuation report"
                    file={report}
                    problem={reportProblem}
                    disabled={resubmitting}
                    onFile={chooseReport}
                    onClear={() => {
                      setReport(null);
                      setReportProblem(null);
                    }}
                  />
                  <DropZone
                    id="resubmit-loi"
                    title="Letter of instruction"
                    file={loi}
                    problem={loiProblem}
                    disabled={resubmitting}
                    onFile={chooseLoi}
                    onClear={() => {
                      setLoi(null);
                      setLoiProblem(null);
                    }}
                  />
                </div>
                {resubmitting ? (
                  <div className="stack" aria-live="polite">
                    <div className="progress">
                      <div className="progress__bar" style={{ width: `${resubmitProgress}%` }} />
                    </div>
                    <span className="muted">Uploading your files… {resubmitProgress}%</span>
                  </div>
                ) : (
                  <Button onClick={() => void submitResubmission()} disabled={!canResubmit} block>
                    Resubmit
                  </Button>
                )}
              </Tile>
            </section>
          </div>
        </div>
      ) : null}
    </main>
  );
}
